/**
 * 安全的 JSON 解析。
 *
 * 背景（`docs/DEFENSIVE-AUDIT.md`）：看板里 `labels` / `settings` / 单元格值这些字段
 * 都可能因为手工改库、旧版本数据、导入脏数据而变成非法 JSON。渲染期间一旦 `JSON.parse`
 * 抛错，React 会卸载整棵树 → 页面纯黑（board #153 就是这么黑的）。
 *
 * 这里统一三件事：
 *   1. 同时接受「字符串」和「已经是对象」两种形态（库里两种都出现过）；
 *   2. 解析失败返回 fallback，绝不抛错；
 *   3. 同一条脏数据只 warn 一次，避免刷屏。
 */

const warned = new Set<string>()

/** 解析 JSON；失败或非对象时返回 fallback。`label` 只用于日志去重。 */
export function safeJsonParse<T>(input: unknown, fallback: T, label?: string): T {
  if (input === null || input === undefined) return fallback
  if (typeof input === 'object') return input as T
  if (typeof input !== 'string') return fallback
  const text = input.trim()
  if (text === '') return fallback
  try {
    const value: unknown = JSON.parse(text)
    if (value === null || value === undefined) return fallback
    return value as T
  } catch {
    const key = label ?? text.slice(0, 48)
    if (!warned.has(key)) {
      warned.add(key)
      console.warn(`[safeJsonParse] 非法 JSON（${key}）`, text.slice(0, 160))
    }
    return fallback
  }
}

/** 解析成对象（数组 / 原始值都算失败），失败返回 fallback 对象 */
export function safeJsonObject<T extends object>(input: unknown, fallback: T, label?: string): T {
  const v = safeJsonParse<unknown>(input, fallback, label)
  if (v === null || typeof v !== 'object' || Array.isArray(v)) return fallback
  return v as T
}

/** 仅测试用：清空去重集合 */
export function __resetSafeJsonWarnings(): void {
  warned.clear()
}
